/**
 * DOB Scroll Picker – cross-platform.
 * Web: styled <select> dropdowns.
 * Native Android/iOS: @react-native-picker/picker (native spinner, no FlatList jank).
 */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';

// Generate arrays
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const YEARS = Array.from({ length: 81 }, (_, i) => 2010 - i); // 2010 down to 1930

const MONTH_LABELS: Record<number, string> = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

interface DobScrollPickerProps {
  month: string;
  day: string;
  year: string;
  onChangeMonth: (v: string) => void;
  onChangeDay: (v: string) => void;
  onChangeYear: (v: string) => void;
  colors: {
    text: string;
    textSecondary: string;
    textTertiary: string;
    surface: string;
    border: string;
    accent: string;
  };
}

export default function DobScrollPicker(props: DobScrollPickerProps) {
  if (Platform.OS === 'web') {
    return <DobSelectWeb {...props} />;
  }
  return <DobScrollNative {...props} />;
}

/* ── Web: native <select> ── */
function DobSelectWeb({ month, day, year, onChangeMonth, onChangeDay, onChangeYear, colors }: DobScrollPickerProps) {
  const selectStyle: React.CSSProperties = {
    flex: 1,
    height: 48,
    backgroundColor: colors.surface,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: 2,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    paddingLeft: 8,
    paddingRight: 8,
    cursor: 'pointer',
  } as any;

  return (
    <View style={styles.webRow}>
      {/* @ts-ignore - web-only */}
      <select
        style={selectStyle}
        value={month}
        onChange={(e: any) => onChangeMonth(e.target.value)}
      >
        <option value="" disabled>Month</option>
        {MONTHS.map((m) => (
          <option key={m} value={String(m)}>{MONTH_LABELS[m]}</option>
        ))}
      </select>
      <Text style={{ color: colors.textTertiary, fontSize: 20, fontWeight: '300', marginHorizontal: 4 }}>/</Text>
      {/* @ts-ignore */}
      <select
        style={selectStyle}
        value={day}
        onChange={(e: any) => onChangeDay(e.target.value)}
      >
        <option value="" disabled>Day</option>
        {DAYS.map((d) => (
          <option key={d} value={String(d)}>{d}</option>
        ))}
      </select>
      <Text style={{ color: colors.textTertiary, fontSize: 20, fontWeight: '300', marginHorizontal: 4 }}>/</Text>
      {/* @ts-ignore */}
      <select
        style={{ ...selectStyle, flex: 1.5 }}
        value={year}
        onChange={(e: any) => onChangeYear(e.target.value)}
      >
        <option value="" disabled>Year</option>
        {YEARS.map((y) => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
    </View>
  );
}

/* ── Native: System spinner picker (works on all Android OEMs) ── */
function DobScrollNative({ month, day, year, onChangeMonth, onChangeDay, onChangeYear, colors }: DobScrollPickerProps) {
  const pickerStyle = {
    color: colors.text,
    backgroundColor: colors.surface,
    flex: 1,
  };
  return (
    <View style={styles.nativeRow}>
      <View style={[styles.pickerCol, { flex: 1.6, borderColor: colors.border, backgroundColor: colors.surface }]}>
        <Text style={[styles.pickerLabel, { color: colors.textSecondary }]}>Month</Text>
        <Picker
          selectedValue={month || ''}
          onValueChange={(v) => v && onChangeMonth(String(v))}
          style={pickerStyle}
          mode="dropdown"
          itemStyle={{ color: colors.text, fontSize: 16 }}
          dropdownIconColor={colors.textSecondary}
          accessibilityLabel="Month"
        >
          <Picker.Item label="Month" value="" color={colors.textTertiary} />
          {MONTHS.map((m) => (
            <Picker.Item key={m} label={MONTH_LABELS[m]} value={String(m)} color={colors.text} />
          ))}
        </Picker>
      </View>
      <View style={[styles.pickerCol, { borderColor: colors.border, backgroundColor: colors.surface }]}>
        <Text style={[styles.pickerLabel, { color: colors.textSecondary }]}>Day</Text>
        <Picker
          selectedValue={day || ''}
          onValueChange={(v) => v && onChangeDay(String(v))}
          style={pickerStyle}
          mode="dropdown"
          itemStyle={{ color: colors.text, fontSize: 16 }}
          dropdownIconColor={colors.textSecondary}
          accessibilityLabel="Day"
        >
          <Picker.Item label="Day" value="" color={colors.textTertiary} />
          {DAYS.map((d) => (
            <Picker.Item key={d} label={String(d)} value={String(d)} color={colors.text} />
          ))}
        </Picker>
      </View>
      <View style={[styles.pickerCol, { flex: 1.3, borderColor: colors.border, backgroundColor: colors.surface }]}>
        <Text style={[styles.pickerLabel, { color: colors.textSecondary }]}>Year</Text>
        <Picker
          selectedValue={year || ''}
          onValueChange={(v) => v && onChangeYear(String(v))}
          style={pickerStyle}
          mode="dropdown"
          itemStyle={{ color: colors.text, fontSize: 16 }}
          dropdownIconColor={colors.textSecondary}
          accessibilityLabel="Year"
        >
          <Picker.Item label="Year" value="" color={colors.textTertiary} />
          {YEARS.map((y) => (
            <Picker.Item key={y} label={String(y)} value={String(y)} color={colors.text} />
          ))}
        </Picker>
      </View>
    </View>
  );
}



const styles = StyleSheet.create({
  webRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  nativeRow: {
    flexDirection: 'row',
    gap: 8,
  },
  pickerCol: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 2,
    overflow: 'hidden',
  },
  pickerLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 2,
    marginTop: 4,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
});
